import React from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  Hero,
  Services,
  Rooms,
  GalleryPreview,
  Facilities,
  Booking,
  Testimonials,
  VideoTestimonials,
  FAQ
} from '../components';

// Elegant Color Palette
const COLORS = {
  primary: '#2563eb',      // Elegant blue
  secondary: '#7c3aed',    // Soft purple
  accent: '#f59e0b',       // Warm amber
  dark: '#1e293b',         // Deep blue
  light: '#f8fafc',        // Light background
  white: '#ffffff',         // Pure white
  gray: '#64748b',         // Muted gray
  lightGray: '#f1f5f9',    // Very light gray
  gradient: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 50%, #2563eb 100%)',
  textGradient: 'linear-gradient(135deg, #1e293b 0%, #2563eb 50%, #7c3aed 100%)'
};

// Premium Animations

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

// Premium Styled Components
const HomePageContainer = styled.div`
  background: linear-gradient(135deg, ${COLORS.white} 0%, ${COLORS.light} 50%, ${COLORS.lightGray} 100%);
  overflow-x: hidden;
  position: relative;
`;

const PremiumSection = styled.section`
  position: relative;
  padding: 3rem 0;
  background: ${props => props.bgColor || COLORS.white};
  overflow: hidden;
  
  &:hover {
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(135deg, 
        rgba(37, 99, 235, 0.02) 0%, 
        rgba(124, 58, 237, 0.05) 100%
      );
      pointer-events: none;
      animation: fadeInUp 0.3s ease-out;
    }
  }
`;

const SectionContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 2;
`;

const PremiumTitle = styled(motion.h2)`
  font-size: clamp(2rem, 4vw, 3.5rem);
  font-weight: 700;
  text-align: center;
  margin-bottom: 1rem;
  background: ${COLORS.textGradient};
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  position: relative;
  letter-spacing: -0.02em;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -12px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background: ${COLORS.gradient};
    border-radius: 2px;
    animation: ${shimmer} 2s ease-in-out infinite;
  }
  
  &:hover {
    transform: translateY(-2px);
    filter: brightness(1.1);
    transition: all 0.3s ease;
  }
`;

const PremiumSubtitle = styled(motion.p)`
  text-align: center;
  color: ${COLORS.gray};
  font-size: 1.125rem;
  margin-bottom: 2rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.7;
  font-weight: 400;
  
  &:hover {
    color: ${COLORS.dark};
    transform: translateY(-1px);
    transition: all 0.3s ease;
  }
`;

const FloatingElements = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: hidden;
`;

const FloatingElement = styled(motion.div)`
  position: absolute;
  width: ${props => props.size || '60px'};
  height: ${props => props.size || '60px'};
  background: linear-gradient(135deg, 
    rgba(124, 58, 237, 0.1) 0%, 
    rgba(37, 99, 235, 0.15) 100%
  );
  border: 1px solid rgba(124, 58, 237, 0.2);
  border-radius: 50%;
  backdrop-filter: blur(15px);
  box-shadow: 0 8px 32px rgba(124, 58, 237, 0.15);
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 12px 40px rgba(124, 58, 237, 0.25);
    transition: all 0.4s ease;
  }
`;

const CTASection = styled.section`
  padding: 4rem 0;
  background: ${COLORS.gradient};
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: url('https://images.unsplash.com/photo-1566073771259-6a8506099945?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80');
    background-size: cover;
    background-position: center;
    opacity: 0.08;
    animation: ${shimmer} 8s ease-in-out infinite;
  }
  
  &:hover {
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(135deg, 
        rgba(124, 58, 237, 0.1) 0%, 
        rgba(37, 99, 235, 0.2) 100%
      );
      pointer-events: none;
      animation: fadeInUp 0.3s ease-out;
    }
  }
`;

const CTAContent = styled.div`
  position: relative;
  z-index: 2;
  text-align: center;
  color: ${COLORS.white};
`;

const CTATitle = styled(motion.h2)`
  font-size: clamp(1.8rem, 3.5vw, 3rem);
  font-weight: 700;
  margin-bottom: 1.5rem;
  text-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  letter-spacing: -0.01em;
  
  &:hover {
    transform: translateY(-3px) scale(1.02);
    text-shadow: 0 6px 25px rgba(0, 0, 0, 0.4);
    transition: all 0.3s ease;
  }
`;

const CTADescription = styled(motion.p)`
  font-size: 1.125rem;
  margin-bottom: 2.5rem;
  opacity: 0.95;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.7;
  font-weight: 400;
  
  &:hover {
    opacity: 1;
    transform: translateY(-1px);
    transition: all 0.3s ease;
  }
`;

const CTAButtons = styled(motion.div)`
  display: flex;
  gap: 1.5rem;
  justify-content: center;
  flex-wrap: wrap;
`;

const PrimaryCTAButton = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  background: ${COLORS.gradient};
  color: ${COLORS.white};
  padding: 1rem 2.5rem;
  border-radius: 50px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-decoration: none;
  box-shadow: 
    0 10px 25px rgba(37, 99, 235, 0.25),
    0 4px 10px rgba(37, 99, 235, 0.15);
  border: 2px solid transparent;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  font-size: 0.95rem;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, 
      transparent 0%, 
      rgba(255, 255, 255, 0.3) 50%, 
      transparent 100%
    );
    transition: left 0.4s ease;
  }
  
  &:hover {
    transform: translateY(-4px) scale(1.05);
    box-shadow: 
      0 15px 35px rgba(37, 99, 235, 0.35),
      0 6px 15px rgba(37, 99, 235, 0.25);
    border-color: rgba(255, 255, 255, 0.3);
    
    &::before {
      left: 100%;
    }
  }
  
  &:active {
    transform: translateY(-2px) scale(1.02);
  }
`;

const SecondaryCTAButton = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(15px);
  color: ${COLORS.white};
  padding: 1rem 2.5rem;
  border-radius: 50px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-decoration: none;
  border: 2px solid rgba(255, 255, 255, 0.3);
  transition: all 0.3s ease;
  font-size: 0.95rem;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-4px) scale(1.05);
    border-color: rgba(255, 255, 255, 0.5);
    backdrop-filter: blur(20px);
  }
  
  &:active {
    transform: translateY(-2px) scale(1.02);
  }
`;

const AnimatedSection = ({ children, delay = 0, animation = 'fadeInUp' }) => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const getAnimation = () => {
    switch(animation) {
      case 'slideInLeft':
        return {
          initial: { opacity: 0, x: -50 },
          animate: inView ? { opacity: 1, x: 0 } : {},
          transition: { duration: 0.8, delay: delay * 0.2, ease: [0.25, 0.46, 0.45, 0.94] }
        };
      case 'slideInRight':
        return {
          initial: { opacity: 0, x: 50 },
          animate: inView ? { opacity: 1, x: 0 } : {},
          transition: { duration: 0.8, delay: delay * 0.2, ease: [0.25, 0.46, 0.45, 0.94] }
        };
      case 'scaleIn':
        return {
          initial: { opacity: 0, scale: 0.8 },
          animate: inView ? { opacity: 1, scale: 1 } : {},
          transition: { duration: 0.7, delay: delay * 0.2, ease: [0.25, 0.46, 0.45, 0.94] }
        };
      default:
        return {
          initial: { opacity: 0, y: 30 },
          animate: inView ? { opacity: 1, y: 0 } : {},
          transition: { duration: 0.8, delay: delay * 0.2, ease: [0.25, 0.46, 0.45, 0.94] }
        };
    }
  };

  return (
    <motion.div
      ref={ref}
      {...getAnimation()}
    >
      {children}
    </motion.div>
  );
};

const HomePage = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
        duration: 0.2
      }
    }
  };

  return (
    <HomePageContainer>
      {/* Hero Section */}
      <Hero />
      
      {/* Services Section with Premium Styling */}
      <PremiumSection bgColor={COLORS.white}>
        <FloatingElements>
          <FloatingElement
            size="50px"
            style={{ top: '15%', left: '8%' }}
            animate={{
              y: [0, -15, 0],
              rotate: [0, 180, 360],
              scale: [1, 1.1, 1]
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            whileHover={{ scale: 1.2, rotate: 45 }}
          />
          <FloatingElement
            size="35px"
            style={{ top: '65%', right: '12%' }}
            animate={{
              y: [0, -10, 0],
              rotate: [0, -180, -360],
              scale: [1, 0.9, 1]
            }}
            transition={{
              duration: 5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 1
            }}
            whileHover={{ scale: 1.3, rotate: -45 }}
          />
          <FloatingElement
            size="70px"
            style={{ bottom: '25%', left: '15%' }}
            animate={{
              y: [0, -20, 0],
              rotate: [0, 360, 0],
              scale: [1, 1.05, 1]
            }}
            transition={{
              duration: 6,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 2
            }}
            whileHover={{ scale: 1.15, rotate: 90 }}
          />
        </FloatingElements>
        
        <SectionContainer>
          <AnimatedSection delay={0.1} animation="slideInLeft">
            <PremiumTitle>
              Exquisite Services
            </PremiumTitle>
            <PremiumSubtitle>
              Experience world-class hospitality tailored to your every need
            </PremiumSubtitle>
          </AnimatedSection>
          
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <Services />
          </motion.div>
        </SectionContainer>
      </PremiumSection>

      {/* Rooms Section */}
      <PremiumSection bgColor={COLORS.light}>
        <SectionContainer>
          <AnimatedSection delay={0.2} animation="slideInRight">
            <PremiumTitle>
              Luxury Accommodations
            </PremiumTitle>
            <PremiumSubtitle>
              Indulge in our elegantly designed rooms with breathtaking lake views
            </PremiumSubtitle>
          </AnimatedSection>
          
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <Rooms />
          </motion.div>
        </SectionContainer>
      </PremiumSection>

      {/* Gallery Preview */}
      <PremiumSection bgColor={COLORS.white}>
        <FloatingElements>
          <FloatingElement
            size="40px"
            style={{ top: '20%', right: '10%' }}
            animate={{
              y: [0, -12, 0],
              rotate: [0, -90, -180],
              opacity: [0.3, 0.7, 0.3]
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            whileHover={{ scale: 1.4, opacity: 1 }}
          />
        </FloatingElements>
        
        <SectionContainer>
          <AnimatedSection delay={0.3} animation="scaleIn">
            <PremiumTitle>
              Visual Journey
            </PremiumTitle>
            <PremiumSubtitle>
              Explore the beauty and elegance of Wular Lake Resort
            </PremiumSubtitle>
          </AnimatedSection>
          
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <GalleryPreview />
          </motion.div>
        </SectionContainer>
      </PremiumSection>

      {/* Facilities Section */}
      <PremiumSection bgColor={COLORS.lightGray}>
        <SectionContainer>
          <AnimatedSection delay={0.4} animation="slideInLeft">
            <PremiumTitle>
              World-Class Facilities
            </PremiumTitle>
            <PremiumSubtitle>
              Everything you need for a perfect lakeside retreat
            </PremiumSubtitle>
          </AnimatedSection>
          
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <Facilities />
          </motion.div>
        </SectionContainer>
      </PremiumSection>

      {/* Testimonials Section */}
      <PremiumSection bgColor={COLORS.white}>
        <FloatingElements>
          <FloatingElement
            size="45px"
            style={{ bottom: '30%', left: '8%' }}
            animate={{
              y: [0, -8, 0],
              rotate: [0, 45, 0],
              scale: [1, 1.2, 1]
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 1.5
            }}
            whileHover={{ scale: 1.5, rotate: 90 }}
          />
        </FloatingElements>
        
        <SectionContainer>
          <AnimatedSection delay={0.5} animation="slideInRight">
            <PremiumTitle>
              Guest Experiences
            </PremiumTitle>
            <PremiumSubtitle>
              Hear what our valued guests have to say about their stay
            </PremiumSubtitle>
          </AnimatedSection>
          
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <Testimonials />
          </motion.div>
        </SectionContainer>
      </PremiumSection>

      {/* Video Testimonials Section */}
      <VideoTestimonials />

      {/* FAQ Section */}
      <PremiumSection bgColor={COLORS.light}>
        <SectionContainer>
          <AnimatedSection delay={0.6} animation="scaleIn">
            <PremiumTitle>
              Frequently Asked Questions
            </PremiumTitle>
            <PremiumSubtitle>
              Everything you need to know about your stay with us
            </PremiumSubtitle>
          </AnimatedSection>
          
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <FAQ />
          </motion.div>
        </SectionContainer>
      </PremiumSection>



      {/* Enhanced Booking Section */}
      <PremiumSection bgColor={COLORS.white} id="booking">
        <SectionContainer>
          <AnimatedSection delay={1.0} animation="slideInLeft">
            <PremiumTitle>
              Reserve Your Paradise
            </PremiumTitle>
            <PremiumSubtitle>
              Simple, secure booking with instant confirmation
            </PremiumSubtitle>
          </AnimatedSection>
          
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <Booking />
          </motion.div>
        </SectionContainer>
      </PremiumSection>
      {/* Premium Booking Section */}
      <CTASection>
        <FloatingElements>
          <FloatingElement
            size="60px"
            style={{ top: '20%', right: '15%' }}
            animate={{
              y: [0, -25, 0],
              rotate: [0, 360, 0],
              opacity: [0.2, 0.5, 0.2]
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            whileHover={{ scale: 1.3, opacity: 0.8 }}
          />
          <FloatingElement
            size="35px"
            style={{ bottom: '25%', left: '10%' }}
            animate={{
              y: [0, -15, 0],
              rotate: [0, -360, 0],
              scale: [1, 0.8, 1]
            }}
            transition={{
              duration: 6,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 2
            }}
            whileHover={{ scale: 1.4, rotate: -90 }}
          />
        </FloatingElements>
        
        <SectionContainer>
          <CTAContent>
            <AnimatedSection delay={0.7} animation="fadeInUp">
              <CTATitle>
                Ready for Your Dream Vacation?
              </CTATitle>
            </AnimatedSection>
            
            <AnimatedSection delay={0.8} animation="fadeInUp">
              <CTADescription>
                Book your stay at Wular Lake Resort and experience the perfect blend of luxury, nature, and Kashmiri hospitality
              </CTADescription>
            </AnimatedSection>
            
            <AnimatedSection delay={0.9} animation="scaleIn">
              <CTAButtons>
                <PrimaryCTAButton
                  href="/booking"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Book Your Stay Now
                </PrimaryCTAButton>
                
                <SecondaryCTAButton
                  href="/contact"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Contact Us
                </SecondaryCTAButton>
              </CTAButtons>
            </AnimatedSection>
          </CTAContent>
        </SectionContainer>
      </CTASection>
    </HomePageContainer>
  );
};

export default HomePage;
